import React, { createContext, useContext, useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { CATALOGUE } from "@/data/i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LanguageContext = createContext(null);

export const LANGS = [
  { code: "en", label: "English", native: "English" },
  { code: "hi", label: "Hindi", native: "हिन्दी" },
  { code: "bn", label: "Bengali", native: "বাংলা" },
];

// localStorage stores ONLY the two-letter language preference (e.g., "en").
// No sensitive or PII data is persisted here.
const STORAGE_KEY = "gi_lang";
const isValidLang = (code) => LANGS.some(l => l.code === code);

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState("en");
  const [dbCatalogue, setDbCatalogue] = useState({});

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (isValidLang(saved)) setLangState(saved);
    } catch { /* localStorage may be disabled (private mode); default to English. */ }
  }, []);

  // Fetch any admin-saved translations from ui_translations. Admin edits made
  // via /admin/translations become live on next page load. Silent fail is OK
  // because CATALOGUE (bundled JS) is our fallback.
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/translations`, { timeout: 6000 })
      .then(r => {
        const map = {};
        (r.data || []).forEach(row => { if (row.key) map[row.key] = row; });
        setDbCatalogue(map);
      })
      .catch(() => { /* fall back to static CATALOGUE */ });
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute("lang", lang);
  }, [lang]);

  const setLang = useCallback((code) => {
    if (!isValidLang(code)) return;
    setLangState(code);
    try { localStorage.setItem(STORAGE_KEY, code); }
    catch { /* localStorage may be disabled; preference stays in-memory. */ }
  }, []);

  const t = useCallback((keyOrObj, fallback = "") => {
    // Key-based lookup: DB catalogue (admin-editable) → static CATALOGUE → fallback.
    if (typeof keyOrObj === "string" && (dbCatalogue[keyOrObj] || CATALOGUE[keyOrObj])) {
      const dbEntry = dbCatalogue[keyOrObj] || {};
      const staticEntry = CATALOGUE[keyOrObj] || {};
      const val = (dbEntry[lang] || "").trim() || (staticEntry[lang] || "").trim();
      if (val) return val;
      return dbEntry.en || staticEntry.en || fallback;
    }
    // Object form: `t({en, hi, bn})` — used for per-book/per-record translations.
    if (!keyOrObj) return fallback;
    if (typeof keyOrObj === "string") return keyOrObj;
    return keyOrObj[lang] || keyOrObj.en || fallback;
  }, [lang, dbCatalogue]);

  // True when the caller passed a catalogue key AND the language-specific
  // value is empty in both DB and static catalogue (i.e. showing English fallback).
  const isFallback = useCallback((key) => {
    if (lang === "en" || typeof key !== "string") return false;
    const dbEntry = dbCatalogue[key];
    const staticEntry = CATALOGUE[key];
    if (!dbEntry && !staticEntry) return false;
    const val = (dbEntry?.[lang] || "").trim() || (staticEntry?.[lang] || "").trim();
    return !val;
  }, [lang, dbCatalogue]);

  // Reports the language actually returned by t(key), following the SAME
  // lookup order used by t: DB[lang] → static[lang] → DB.en → static.en.
  // Returns null when the key isn't in the catalogue at all.
  const renderedLang = useCallback((key) => {
    if (typeof key !== "string") return null;
    const dbEntry = dbCatalogue[key];
    const staticEntry = CATALOGUE[key];
    if (!dbEntry && !staticEntry) return null;
    if ((dbEntry?.[lang] || "").trim() || (staticEntry?.[lang] || "").trim()) return lang;
    if ((dbEntry?.en || "").trim() || (staticEntry?.en || "").trim()) return "en";
    return null;
  }, [lang, dbCatalogue]);

  const hasTranslation = useCallback((obj) => Boolean(obj && obj[lang] && lang !== "en"), [lang]);

  const value = useMemo(
    () => ({ lang, setLang, t, isFallback, renderedLang, hasTranslation, LANGS }),
    [lang, setLang, t, isFallback, renderedLang, hasTranslation],
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLang = () => useContext(LanguageContext);
